import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ExcelJS from 'exceljs';
import { Portal } from '../components/Portal';
import { InteractiveCampaignModal } from '../components/InteractiveCampaignModal';
import {
  interactiveCampaignApi,
  InteractiveCampaign,
  Connection,
} from '../services/interactiveCampaignApi';
import { SimpleTooltip } from '../components/ui/SimpleTooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const StatusBadge = ({ status }: { status: string }) => {
  const styles: Record<string, string> = {
    STARTED: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    SCHEDULED: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    PAUSED: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    COMPLETED: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
    DRAFT: 'bg-white/10 text-gray-400 border-white/10',
  };

  const labels: Record<string, string> = {
    STARTED: '✅ Iniciada',
    SCHEDULED: '📅 Aguardando',
    PAUSED: '⏸️ Pausada',
    COMPLETED: '✓ Finalizada',
    DRAFT: '📝 Rascunho',
  };

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${styles[status] || styles.DRAFT} shadow-sm backdrop-blur-sm`}>
      {labels[status] || labels.DRAFT}
    </span>
  );
};

// Função helper para remover @c.us dos telefones
const cleanPhoneNumber = (phone: string): string => {
  return phone ? phone.replace(/@c\.us$/, '') : phone;
};

interface CampaignReportData {
  campaign: {
    id: string;
    name: string;
    status: string;
    createdAt: string;
    connection?: {
      instanceName: string;
    };
    sessions: Array<{
      id: string;
      contactName: string;
      contactPhone: string;
      status: string;
      visitedNodes?: Record<string, {
        sent: boolean;
        visitedAt: string;
        error?: string;
        surveyData?: {
          rating?: number;
          text?: string;
          submitted?: boolean;
          submittedAt?: string;
        };
      }>;
      variables?: Record<string, unknown>;
    }>;
  };
  stats: {
    total: number;
    active: number;
    completed: number;
    failed: number;
    expired: number;
    survey?: {
      totalResponses: number;
      averageRating: number;
      ratingDistribution: Record<number, number>;
    };
  };
  flowNodes?: Array<{
    id: string;
    label: string;
    type: string;
  }>;
}

interface GraphNode {
  id: string;
  data?: {
    nodeType?: string;
    config?: {
      connections?: string[];
    };
  };
  [key: string]: unknown;
}

export function InteractiveCampaignPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<InteractiveCampaign[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<InteractiveCampaign[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    type: 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    type: 'info'
  });

  const showConfirm = useCallback((config: {
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    type?: 'danger' | 'info';
  }) => {
    setConfirmDialog({
      isOpen: true,
      title: config.title,
      message: config.message,
      onConfirm: config.onConfirm,
      confirmText: config.confirmText,
      type: config.type || 'info'
    });
  }, []);

  // Estados para o modal de relatório
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportData, setReportData] = useState<CampaignReportData | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'details'>('dashboard');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [campaignsData, connectionsData] = await Promise.all([
        interactiveCampaignApi.getCampaigns(),
        interactiveCampaignApi.getConnections(),
      ]);
      setAllCampaigns(campaignsData);
      setCampaigns(campaignsData);
      setConnections(connectionsData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao carregar dados: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    // Auto-refresh a cada 30 segundos
    const intervalId = setInterval(() => {
      loadData();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [loadData]);

  // Filtrar campanhas baseado na busca e filtro de status
  useEffect(() => {
    let filtered = allCampaigns;

    // Filtro de busca
    if (searchTerm) {
      filtered = filtered.filter(campaign =>
        campaign.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro de status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(campaign => campaign.status === statusFilter);
    }

    setCampaigns(filtered);
  }, [searchTerm, statusFilter, allCampaigns]);

  const handleCreateCampaign = useCallback(async (name: string) => {
    try {
      const newCampaign = await interactiveCampaignApi.createCampaign({
        name,
        graph: {
          nodes: [],
          edges: [],
        },
      });

      toast.success('Campanha criada com sucesso! Configure o Trigger para definir conexões e categorias.');

      // Redirecionar para o builder
      navigate(`/campanhas/interativa/${newCampaign.id}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao criar campanha: ${errorMessage}`);
    }
  }, [navigate]);

  const handleEditCampaign = useCallback((id: string) => {
    navigate(`/campanhas/interativa/${id}`);
  }, [navigate]);

  const handleDeleteCampaign = useCallback(async (id: string, name: string) => {
    showConfirm({
      title: 'Excluir Campanha',
      message: `Tem certeza que deseja excluir a campanha "${name}"?`,
      confirmText: 'Excluir',
      type: 'danger',
      onConfirm: async () => {
        try {
          setDeletingId(id);
          await interactiveCampaignApi.deleteCampaign(id);
          toast.success('Campanha excluída com sucesso!');
          await loadData();
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
          toast.error(`Erro ao excluir campanha: ${errorMessage}`);
        } finally {
          setDeletingId(null);
        }
      }
    });
  }, [loadData, showConfirm]);

  const handlePublishCampaign = useCallback(async (id: string, name: string) => {
    showConfirm({
      title: 'Publicar Campanha',
      message: `Deseja publicar a campanha "${name}"? Ela começará a processar mensagens.`,
      confirmText: 'Publicar',
      type: 'info',
      onConfirm: async () => {
        try {
          await interactiveCampaignApi.publishCampaign(id);
          toast.success('Campanha publicada!');
          await loadData();
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
          toast.error(`Erro ao publicar campanha: ${errorMessage}`);
        }
      }
    });
  }, [loadData, showConfirm]);

  const handlePauseCampaign = useCallback(async (id: string, name: string) => {
    try {
      await interactiveCampaignApi.pauseCampaign(id);
      toast.success(`Campanha "${name}" pausada!`);
      await loadData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao pausar campanha: ${errorMessage}`);
    }
  }, [loadData]);

  const handleCompleteCampaign = useCallback(async (id: string, name: string) => {
    showConfirm({
      title: 'Finalizar Campanha',
      message: `Tem certeza que deseja finalizar a campanha "${name}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Finalizar',
      type: 'danger',
      onConfirm: async () => {
        try {
          await interactiveCampaignApi.completeCampaign(id);
          toast.success(`Campanha "${name}" finalizada!`);
          await loadData();
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
          toast.error(`Erro ao finalizar campanha: ${errorMessage}`);
        }
      }
    });
  }, [loadData, showConfirm]);

  const handleDuplicateCampaign = useCallback(async (id: string, name: string) => {
    try {
      const duplicatedCampaign = await interactiveCampaignApi.duplicateCampaign(id);
      toast.success(`Campanha "${name}" duplicada com sucesso!`);
      await loadData();

      // Redirecionar para editar a campanha duplicada
      navigate(`/campanhas/interativa/${duplicatedCampaign.id}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao duplicar campanha: ${errorMessage}`);
    }
  }, [loadData, navigate]);

  const handleViewReport = useCallback(async (id: string) => {
    setReportLoading(true);
    setShowReportModal(true);

    try {
      const data = await interactiveCampaignApi.getCampaignReport(id);
      setReportData(data as unknown as CampaignReportData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao carregar relatório: ${errorMessage}`);
      setShowReportModal(false);
    } finally {
      setReportLoading(false);
    }
  }, []);

  const handleExportToExcel = useCallback(async () => {
    if (!reportData) return;

    try {
      // Preparar dados para exportação
      const exportData = reportData.campaign.sessions.map((session) => {
        const row: Record<string, string> = {
          'Nome': session.contactName,
          'Telefone': cleanPhoneNumber(session.contactPhone),
          'Status': session.status === 'ACTIVE' ? 'Ativa' :
            session.status === 'COMPLETED' ? 'Concluída' :
              session.status === 'FAILED' ? 'Falhada' : 'Expirada',
          'Pesquisa Rating': '',
          'Pesquisa Comentário': '',
          'Pesquisa Enviada': 'Não',
        };

        // Adicionar colunas para cada nó do fluxo
        if (reportData.flowNodes) {
          reportData.flowNodes.forEach((node) => {
            const nodeData = session.visitedNodes?.[node.id];
            if (nodeData) {
              row[`${node.label} (${node.type})`] = nodeData.sent ?
                `✓ ${new Date(nodeData.visitedAt).toLocaleString('pt-BR')}` :
                `✗ ${nodeData.error || 'Falhou'}`;

              // Extrair dados de pesquisa se existirem
              if (nodeData.surveyData) {
                if (nodeData.surveyData.rating !== undefined) {
                  row['Pesquisa Rating'] = nodeData.surveyData.rating.toString();
                }
                if (nodeData.surveyData.text) {
                  row['Pesquisa Comentário'] = nodeData.surveyData.text;
                }
                if (nodeData.surveyData.submitted) {
                  row['Pesquisa Enviada'] = 'Sim';
                }
              }
            } else {
              row[`${node.label} (${node.type})`] = '-';
            }
          });
        }

        return row;
      });

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Relatório');

      const headers = Object.keys(exportData[0] || {});
      worksheet.columns = headers.map((header) => {
        if (header === 'Nome') return { header, key: header, width: 20 };
        if (header === 'Telefone') return { header, key: header, width: 15 };
        if (header === 'Status') return { header, key: header, width: 12 };
        if (header === 'Pesquisa Rating') return { header, key: header, width: 15 };
        if (header === 'Pesquisa Comentário') return { header, key: header, width: 30 };
        if (header === 'Pesquisa Enviada') return { header, key: header, width: 15 };
        return { header, key: header, width: 25 };
      });

      exportData.forEach((row) => worksheet.addRow(row));

      // Gerar arquivo e fazer download (browser)
      const fileName = `relatorio_${reportData.campaign.name}_${new Date().toISOString().split('T')[0]}.xlsx`;
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao exportar: ${errorMessage}`);
    }
  }, [reportData]);

  const handleRefreshReport = useCallback(async () => {
    if (!reportData?.campaign?.id) return;
    setReportLoading(true);
    try {
      const data = await interactiveCampaignApi.getCampaignReport(reportData.campaign.id);
      setReportData(data as unknown as CampaignReportData);
      toast.success('Relatório atualizado!');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error(`Erro ao atualizar relatório: ${errorMessage}`);
    } finally {
      setReportLoading(false);
    }
  }, [reportData]);

  const handleToggleSelectCampaign = useCallback((campaignId: string) => {
    setSelectedCampaignIds(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  }, []);

  const handleSelectAllCampaigns = useCallback(() => {
    if (selectedCampaignIds.length === campaigns.length) {
      setSelectedCampaignIds([]);
    } else {
      setSelectedCampaignIds(campaigns.map(c => c.id));
    }
  }, [campaigns, selectedCampaignIds.length]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedCampaignIds.length === 0) return;

    showConfirm({
      title: 'Excluir Campanhas',
      message: `Tem certeza que deseja excluir ${selectedCampaignIds.length} campanha(s) selecionada(s)?`,
      confirmText: 'Excluir',
      type: 'danger',
      onConfirm: async () => {
        try {
          setBulkDeleting(true);

          // Deletar todas as campanhas selecionadas
          await Promise.all(
            selectedCampaignIds.map(id => interactiveCampaignApi.deleteCampaign(id))
          );

          toast.success(`${selectedCampaignIds.length} campanha(s) excluída(s) com sucesso!`);
          setSelectedCampaignIds([]);
          await loadData();
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
          toast.error(`Erro ao excluir campanhas: ${errorMessage}`);
        } finally {
          setBulkDeleting(false);
        }
      }
    });
  }, [selectedCampaignIds, showConfirm, loadData]);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setStatusFilter('all');
  }, []);



  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // Extrai as conexões configuradas no nó Trigger
  const getTriggerConnections = useCallback((campaign: InteractiveCampaign) => {
    const graph = campaign.graph;
    if (!graph || !graph.nodes) return [];

    const triggerNode = graph.nodes.find((n) => (n as unknown as GraphNode).data?.nodeType === 'trigger') as unknown as GraphNode;
    if (!triggerNode || !triggerNode.data?.config) return [];

    return (triggerNode.data.config.connections as string[]) || [];
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1e3a5f] to-black">
      {/* Header Premium */}
      <div className="w-full p-4 z-10 sticky top-0 bg-[#1e3a5f]/80 backdrop-blur-xl border-b border-white/10 rounded-b-[2rem]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold text-white font-oswald tracking-tight mb-2">
              ⚡ Campanhas Interativas
            </h1>
            <p className="text-gray-300 font-medium flex items-center gap-2">
              {selectedCampaignIds.length > 0
                ? <span className="text-blue-400 font-bold">{selectedCampaignIds.length} selecionada(s)</span>
                : <span>{allCampaigns.length} campanhas cadastradas</span>
              }
            </p>
          </div>

          <div className="flex gap-4">
            {selectedCampaignIds.length > 0 ? (
              <>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="px-6 py-3 bg-red-500/20 text-red-300 border border-red-500/30 rounded-xl hover:bg-red-500/30 hover:shadow-lg hover:shadow-red-500/10 transition-all font-bold tracking-wide flex items-center gap-2"
                >
                  {bulkDeleting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-300"></div>
                  ) : (
                    <span>🗑️ EXCLUIR SELECIONADAS</span>
                  )}
                </button>
                <button
                  onClick={() => setSelectedCampaignIds([])}
                  className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all font-bold tracking-wide border border-white/10"
                >
                  CANCELAR
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 font-bold font-oswald tracking-wide flex items-center gap-2 transform hover:-translate-y-0.5"
              >
                <span>+ NOVA CAMPANHA</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 relative">
        <div className="max-w-7xl mx-auto space-y-4 pb-20">
          {/* Filtros Glassmorphism */}
          <div className="bg-white/10 backdrop-blur-xl p-6 rounded-3xl border border-white/20 shadow-lg relative overflow-hidden group">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Buscar campanhas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium text-white placeholder-gray-400"
                />
                <span className="absolute left-4 top-4.5 text-xl opacity-50 grayscale invert">🔍</span>
              </div>

              <div className="w-full md:w-64 relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium text-white appearance-none cursor-pointer [&>option]:text-gray-900"
                >
                  <option value="all">📁 Todos os Status</option>
                  <option value="DRAFT">📝 Rascunho</option>
                  <option value="SCHEDULED">📅 Agendada</option>
                  <option value="STARTED">✅ Iniciada</option>
                  <option value="PAUSED">⏸️ Pausada</option>
                  <option value="COMPLETED">✓ Finalizada</option>
                </select>
                <div className="absolute right-4 top-5 pointer-events-none opacity-50 text-white">▼</div>
              </div>

              {(searchTerm || statusFilter !== 'all') && (
                <button
                  onClick={handleClearFilters}
                  className="px-6 py-4 text-white bg-white/10 border border-white/10 rounded-2xl hover:bg-white/20 transition-colors font-bold tracking-wide"
                >
                  LIMPAR
                </button>
              )}
            </div>

            {/* Seleção em massa */}
            {campaigns.length > 0 && (
              <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between">
                <button
                  onClick={handleSelectAllCampaigns}
                  className="text-sm text-blue-400 hover:text-blue-300 font-bold hover:underline transition-all"
                >
                  {selectedCampaignIds.length === campaigns.length ? 'Desmarcar todas' : 'Selecionar todas'}
                </button>
                {selectedCampaignIds.length > 0 && (
                  <span className="text-sm font-medium text-white bg-white/10 px-3 py-1 rounded-full border border-white/10">
                    {selectedCampaignIds.length} selecionadas
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Lista de Campanhas */}
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-lg overflow-hidden animate-fadeIn">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
                <span className="text-gray-300 font-medium animate-pulse">Carregando campanhas...</span>
              </div>
              // ... (resto do código igual)
            ) : campaigns.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/10">
                  <svg
                    className="h-10 w-10 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 className="mt-2 text-xl font-bold text-white font-oswald tracking-wide">
                  Nenhuma campanha interativa
                </h3>
                <p className="mt-2 text-sm text-gray-400 max-w-sm mx-auto">
                  Crie campanhas inteligentes que interagem automaticamente com seus clientes através de fluxos de conversa.
                </p>
                <div className="mt-8">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300 font-bold font-oswald tracking-wide transform hover:-translate-y-0.5"
                  >
                    + CRIAR PRIMEIRA CAMPANHA
                  </button>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-white/10">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-3 w-12">
                        <input
                          type="checkbox"
                          checked={selectedCampaignIds.length === campaigns.length && campaigns.length > 0}
                          onChange={handleSelectAllCampaigns}
                          className="h-4 w-4 text-blue-500 focus:ring-blue-500 border-white/20 rounded cursor-pointer bg-white/5"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-white uppercase tracking-wider font-oswald">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-white uppercase tracking-wider font-oswald">
                        Conexões
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-white uppercase tracking-wider font-oswald">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-white uppercase tracking-wider font-oswald">
                        Criada em
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-bold text-white uppercase tracking-wider font-oswald">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10 bg-transparent">
                    {campaigns.map((campaign) => (
                      <tr
                        key={campaign.id}
                        className="group bg-white/5 backdrop-blur-sm border-b border-white/10 hover:bg-white/10 transition-all duration-300"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedCampaignIds.includes(campaign.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleSelectCampaign(campaign.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-blue-500 focus:ring-blue-500 border-white/20 rounded cursor-pointer bg-white/5"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-500/20 rounded-xl border border-blue-500/20">
                              <svg
                                className="h-6 w-6 text-blue-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 10V3L4 14h7v7l9-11h-7z"
                                />
                              </svg>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-white font-oswald tracking-wide">
                                {campaign.name}
                              </div>
                              <div className="text-xs text-gray-400 font-medium">
                                {campaign.graph?.nodes?.length || 0} nodes
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                          {(() => {
                            const triggerConnectionIds = getTriggerConnections(campaign);
                            if (triggerConnectionIds.length === 0) {
                              return <div className="text-sm text-gray-500 italic">Nenhuma</div>;
                            }

                            const triggerConnections = connections.filter(c =>
                              triggerConnectionIds.includes(c.id)
                            );

                            if (triggerConnections.length === 0) {
                              return <div className="text-sm text-gray-500 italic">Nenhuma</div>;
                            }

                            return (
                              <div>
                                <div className="text-sm text-gray-300 font-medium">
                                  {triggerConnections[0].instanceName}
                                </div>
                                {triggerConnections.length > 1 && (
                                  <div className="text-xs text-blue-400 font-medium">
                                    +{triggerConnections.length - 1} outra{triggerConnections.length > 2 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                          <StatusBadge status={campaign.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 cursor-pointer" onClick={() => handleEditCampaign(campaign.id)}>
                          {formatDate(campaign.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">

                            {campaign.status === 'DRAFT' && (
                              <SimpleTooltip content="Publicar">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePublishCampaign(campaign.id, campaign.name);
                                  }}
                                  className="text-green-400 hover:text-green-300 p-2 rounded-lg hover:bg-green-500/10 transition-colors"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                              </SimpleTooltip>
                            )}
                            {(campaign.status === 'STARTED' || campaign.status === 'SCHEDULED') && (
                              <SimpleTooltip content="Pausar">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePauseCampaign(campaign.id, campaign.name);
                                  }}
                                  className="text-yellow-400 hover:text-yellow-300 p-2 rounded-lg hover:bg-yellow-500/10 transition-colors"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                              </SimpleTooltip>
                            )}
                            {(campaign.status === 'STARTED' || campaign.status === 'SCHEDULED' || campaign.status === 'PAUSED') && (
                              <SimpleTooltip content="Finalizar">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCompleteCampaign(campaign.id, campaign.name);
                                  }}
                                  className="text-gray-400 hover:text-gray-300 p-2 rounded-lg hover:bg-white/10 transition-colors"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                              </SimpleTooltip>
                            )}
                            <SimpleTooltip content="Editar">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditCampaign(campaign.id);
                                }}
                                className="text-blue-400 hover:text-blue-300 p-2 rounded-lg hover:bg-blue-500/10 transition-colors"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            </SimpleTooltip>
                            <SimpleTooltip content="Ver Relatório">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewReport(campaign.id);
                                }}
                                className="text-purple-400 hover:text-purple-300 p-2 rounded-lg hover:bg-purple-500/10 transition-colors"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </button>
                            </SimpleTooltip>
                            <SimpleTooltip content="Duplicar">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDuplicateCampaign(campaign.id, campaign.name);
                                }}
                                className="text-cyan-400 hover:text-cyan-300 p-2 rounded-lg hover:bg-cyan-500/10 transition-colors"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </button>
                            </SimpleTooltip>
                            <SimpleTooltip content="Excluir">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCampaign(campaign.id, campaign.name);
                                }}
                                disabled={deletingId === campaign.id}
                                className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-500/10 transition-colors disabled:opacity-50"
                              >
                                {deletingId === campaign.id ? (
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-400"></div>
                                ) : (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                )}
                              </button>
                            </SimpleTooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal de Relatório */}
        {showReportModal && (
          <Portal>
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center backdrop-blur-sm" style={{ zIndex: 9999 }}>
              <div className="bg-[#1e3a5f] border border-white/20 rounded-2xl shadow-2xl max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto relative" style={{ zIndex: 1000 }}>
                <div className="flex justify-between items-center p-6 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-bold text-white font-oswald">Relatório da Campanha Interativa</h3>
                    <button
                      onClick={handleRefreshReport}
                      disabled={reportLoading}
                      className="flex items-center gap-2 px-3 py-1 bg-blue-500/20 text-blue-300 border border-blue-500/30 text-sm rounded-lg hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      title="Atualizar relatório"
                    >
                      <svg className={`w-4 h-4 ${reportLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      {reportLoading ? 'Atualizando...' : 'Atualizar'}
                    </button>
                  </div>
                  <button
                    onClick={() => setShowReportModal(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Tabs */}
                <div className="border-b border-white/10">
                  <nav className="flex -mb-px px-6">
                    <button
                      onClick={() => setActiveTab('dashboard')}
                      className={`py-4 px-6 text-sm font-bold font-oswald border-b-2 transition-colors ${activeTab === 'dashboard'
                        ? 'border-blue-500 text-blue-400'
                        : 'border-transparent text-gray-400 hover:text-white hover:border-white/30'
                        }`}
                    >
                      📊 Dashboard
                    </button>
                    <button
                      onClick={() => setActiveTab('details')}
                      className={`py-4 px-6 text-sm font-bold font-oswald border-b-2 transition-colors ${activeTab === 'details'
                        ? 'border-blue-500 text-blue-400'
                        : 'border-transparent text-gray-400 hover:text-white hover:border-white/30'
                        }`}
                    >
                      📋 Detalhes
                    </button>
                  </nav>
                </div>

                <div className="p-6">
                  {reportLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                      <span className="ml-2 text-gray-300">Carregando relatório...</span>
                    </div>
                  ) : reportData ? (
                    <div className="space-y-6">
                      {/* Tab Dashboard */}
                      {activeTab === 'dashboard' && (
                        <div className="space-y-6">
                          {/* Header com informações da campanha */}
                          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white shadow-lg border border-white/10">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="text-2xl font-bold mb-2 font-oswald">{reportData.campaign.name}</h3>
                                <p className="text-blue-100 text-sm">
                                  📅 Criada em {new Date(reportData.campaign.createdAt).toLocaleDateString('pt-BR')}
                                </p>
                              </div>
                              <div className="text-right">
                                <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold border ${reportData.campaign.status === 'DRAFT' ? 'bg-gray-500/30 border-gray-400/30' :
                                  reportData.campaign.status === 'SCHEDULED' ? 'bg-yellow-500/30 border-yellow-400/30' :
                                    reportData.campaign.status === 'STARTED' ? 'bg-green-500/30 border-green-400/30' :
                                      reportData.campaign.status === 'PAUSED' ? 'bg-orange-500/30 border-orange-400/30' :
                                        'bg-blue-500/30 border-blue-400/30'
                                  } backdrop-blur-sm`}>
                                  {reportData.campaign.status === 'DRAFT' ? '📝 Rascunho' :
                                    reportData.campaign.status === 'SCHEDULED' ? '⏰ Agendada' :
                                      reportData.campaign.status === 'STARTED' ? '✅ Iniciada' :
                                        reportData.campaign.status === 'PAUSED' ? '⏸️ Pausada' : '✅ Concluída'}
                                </div>
                                <p className="text-blue-100 text-sm mt-2">
                                  📱 {reportData.campaign.connection?.instanceName || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* KPIs Principais - Cards Grandes */}
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 shadow-lg hover:bg-white/10 transition-all">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-blue-300 text-sm font-bold font-oswald">Total de Contatos</span>
                                <span className="text-3xl">👥</span>
                              </div>
                              <div className="text-3xl font-bold text-white">{reportData.stats.total}</div>
                              <p className="text-xs text-blue-300/70 mt-1">Contatos alcançados</p>
                            </div>

                            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 shadow-lg hover:bg-white/10 transition-all">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-green-300 text-sm font-bold font-oswald">Sessões Ativas</span>
                                <span className="text-3xl">⚡</span>
                              </div>
                              <div className="text-3xl font-bold text-white">{reportData.stats.active}</div>
                              <p className="text-xs text-green-300/70 mt-1">
                                {reportData.stats.total > 0 ? `${((reportData.stats.active / reportData.stats.total) * 100).toFixed(1)}%` : '0%'} do total
                              </p>
                            </div>

                            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 shadow-lg hover:bg-white/10 transition-all">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-purple-300 text-sm font-bold font-oswald">Concluídas</span>
                                <span className="text-3xl">✅</span>
                              </div>
                              <div className="text-3xl font-bold text-white">{reportData.stats.completed}</div>
                              <p className="text-xs text-purple-600 mt-1">
                                {reportData.stats.total > 0 ? `${((reportData.stats.completed / reportData.stats.total) * 100).toFixed(1)}%` : '0%'} taxa de conclusão
                              </p>
                            </div>

                            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 shadow-lg hover:bg-white/10 transition-all">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-red-300 text-sm font-bold font-oswald">Falhadas</span>
                                <span className="text-3xl">❌</span>
                              </div>
                              <div className="text-3xl font-bold text-white">{reportData.stats.failed}</div>
                              <p className="text-xs text-red-300/70 mt-1">
                                {reportData.stats.total > 0 ? `${((reportData.stats.failed / reportData.stats.total) * 100).toFixed(1)}%` : '0%'} taxa de erro
                              </p>
                            </div>
                          </div>

                          {/* Dashboards de Pesquisa (CSAT/NPS) */}
                          {reportData.stats.survey && reportData.stats.survey.totalResponses > 0 && (
                            <div className="space-y-6">
                              <h4 className="text-xl font-bold text-white flex items-center gap-2 font-oswald mt-8">
                                <span className="text-2xl">⭐</span>
                                Resultados da Pesquisa
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Média de Rating */}
                                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-2xl border border-amber-500/20 p-6 shadow-lg backdrop-blur-sm">
                                  <div className="flex justify-between items-start mb-4">
                                    <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center text-2xl">⭐</div>
                                    <div className="text-right">
                                      <p className="text-sm text-amber-300 font-bold uppercase tracking-wider">Média CSAT</p>
                                      <h3 className="text-3xl font-black text-white">{reportData.stats.survey.averageRating.toFixed(1)}</h3>
                                    </div>
                                  </div>
                                  <div className="flex gap-1 justify-center mt-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <span key={star} className={`text-xl ${star <= Math.round(reportData.stats.survey?.averageRating || 0) ? 'text-amber-400' : 'text-gray-600'}`}>
                                        ★
                                      </span>
                                    ))}
                                  </div>
                                </div>

                                {/* Total de Respostas */}
                                <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-2xl border border-blue-500/20 p-6 shadow-lg backdrop-blur-sm">
                                  <div className="flex justify-between items-start mb-4">
                                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center text-2xl">📝</div>
                                    <div className="text-right">
                                      <p className="text-sm text-blue-300 font-bold uppercase tracking-wider">Total Respostas</p>
                                      <h3 className="text-3xl font-black text-white">{reportData.stats.survey.totalResponses}</h3>
                                    </div>
                                  </div>
                                  <p className="text-xs text-blue-300/70 text-center mt-2">
                                    {((reportData.stats.survey.totalResponses / reportData.stats.total) * 100).toFixed(1)}% de taxa de resposta
                                  </p>
                                </div>

                                {/* Distribuição de Ratings */}
                                <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 rounded-2xl border border-purple-500/20 p-6 shadow-lg backdrop-blur-sm">
                                  <h5 className="text-sm text-purple-300 font-bold uppercase tracking-wider mb-4">Distribuição</h5>
                                  <div className="space-y-2">
                                    {[5, 4, 3, 2, 1].map((rating) => {
                                      const count = reportData.stats.survey?.ratingDistribution[rating] || 0;
                                      const percentage = (count / (reportData.stats.survey?.totalResponses || 1)) * 100;
                                      return (
                                        <div key={rating} className="flex items-center gap-2">
                                          <span className="text-xs text-gray-400 w-4">{rating}★</span>
                                          <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                                            <div
                                              className="h-full bg-purple-500 rounded-full"
                                              style={{ width: `${percentage}%` }}
                                            />
                                          </div>
                                          <span className="text-xs text-gray-400 w-8 text-right">{count}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Funil de Conversão por Nó */}
                          {reportData.flowNodes && reportData.flowNodes.length > 0 && (
                            <div className="bg-white/5 rounded-2xl border border-white/10 p-6 shadow-lg backdrop-blur-sm">
                              <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2 font-oswald">
                                <span className="text-2xl">📈</span>
                                Funil de Conversão por Etapa
                              </h4>
                              <div className="space-y-3">
                                {reportData.flowNodes.map((node, index) => {
                                  // Calcular quantos contatos chegaram neste nó
                                  const reachedCount = reportData.campaign.sessions.filter((session) =>
                                    session.visitedNodes?.[node.id]
                                  ).length;
                                  const sentCount = reportData.campaign.sessions.filter((session) =>
                                    session.visitedNodes?.[node.id]?.sent
                                  ).length;
                                  const percentage = reportData.stats.total > 0 ? (reachedCount / reportData.stats.total) * 100 : 0;
                                  const successRate = reachedCount > 0 ? (sentCount / reachedCount) * 100 : 0;

                                  return (
                                    <div key={node.id} className="relative">
                                      <div className="flex items-center gap-4">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold shadow-md border border-white/10">
                                          {index + 1}
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2">
                                              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${node.type === 'text' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                                                node.type === 'image' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                                                  node.type === 'video' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
                                                    node.type === 'audio' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
                                                      node.type === 'document' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
                                                        'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                                                }`}>
                                                {node.type.toUpperCase()}
                                              </span>
                                              <span className="font-bold text-white">{node.label}</span>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm">
                                              <span className="text-gray-400">
                                                <strong className="text-blue-400">{reachedCount}</strong> alcançados
                                              </span>
                                              <span className="text-gray-400">
                                                <strong className="text-green-400">{sentCount}</strong> enviados
                                              </span>
                                              <span className="font-bold text-purple-400">
                                                {percentage.toFixed(1)}%
                                              </span>
                                            </div>
                                          </div>
                                          <div className="relative">
                                            <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
                                              <div
                                                className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                                                style={{ width: `${percentage}%` }}
                                              >
                                                {percentage > 10 && (
                                                  <span className="text-xs font-bold text-white drop-shadow-md">{percentage.toFixed(1)}%</span>
                                                )}
                                              </div>
                                            </div>
                                            {/* Barra de sucesso sobreposta */}
                                            <div className="absolute top-0 left-0 w-full h-3">
                                              <div
                                                className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full opacity-60"
                                                style={{ width: `${(sentCount / reportData.stats.total) * 100}%` }}
                                              />
                                            </div>
                                          </div>
                                          {reachedCount > 0 && (
                                            <p className="text-xs text-gray-400 mt-1">
                                              Taxa de sucesso: <strong className="text-green-400">{successRate.toFixed(1)}%</strong>
                                              {reachedCount - sentCount > 0 && (
                                                <span className="text-red-400 ml-2">
                                                  ({reachedCount - sentCount} falhas)
                                                </span>
                                              )}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      {index < (reportData.flowNodes?.length || 0) - 1 && (
                                        <div className="ml-6 my-2">
                                          <div className="w-0.5 h-4 bg-gradient-to-b from-white/20 to-white/10"></div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Métricas de Performance */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Taxa de Conversão Geral */}
                            <div className="bg-white/5 rounded-2xl border border-white/10 p-6 shadow-lg backdrop-blur-sm">
                              <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2 font-oswald">
                                <span className="text-2xl">🎯</span>
                                Taxa de Conversão
                              </h4>
                              <div className="space-y-4">
                                <div>
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-gray-400 font-medium">Conclusão da Jornada</span>
                                    <span className="text-lg font-bold text-purple-400">
                                      {reportData.stats.total > 0 ? ((reportData.stats.completed / reportData.stats.total) * 100).toFixed(1) : '0'}%
                                    </span>
                                  </div>
                                  <div className="w-full bg-white/10 rounded-full h-3">
                                    <div
                                      className="h-full bg-gradient-to-r from-purple-500 to-purple-700 rounded-full shadow-lg"
                                      style={{ width: `${reportData.stats.total > 0 ? (reportData.stats.completed / reportData.stats.total) * 100 : 0}%` }}
                                    />
                                  </div>
                                </div>

                                <div>
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-gray-400 font-medium">Engajamento Ativo</span>
                                    <span className="text-lg font-bold text-green-400">
                                      {reportData.stats.total > 0 ? ((reportData.stats.active / reportData.stats.total) * 100).toFixed(1) : '0'}%
                                    </span>
                                  </div>
                                  <div className="w-full bg-white/10 rounded-full h-3">
                                    <div
                                      className="h-full bg-gradient-to-r from-green-500 to-green-700 rounded-full shadow-lg"
                                      style={{ width: `${reportData.stats.total > 0 ? (reportData.stats.active / reportData.stats.total) * 100 : 0}%` }}
                                    />
                                  </div>
                                </div>

                                <div>
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-gray-400 font-medium">Taxa de Erro</span>
                                    <span className="text-lg font-bold text-red-400">
                                      {reportData.stats.total > 0 ? ((reportData.stats.failed / reportData.stats.total) * 100).toFixed(1) : '0'}%
                                    </span>
                                  </div>
                                  <div className="w-full bg-white/10 rounded-full h-3">
                                    <div
                                      className="h-full bg-gradient-to-r from-red-500 to-red-700 rounded-full shadow-lg"
                                      style={{ width: `${reportData.stats.total > 0 ? (reportData.stats.failed / reportData.stats.total) * 100 : 0}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Principais Insights */}
                            <div className="bg-white/5 rounded-2xl border border-white/10 p-6 shadow-lg backdrop-blur-sm">
                              <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2 font-oswald">
                                <span className="text-2xl">💡</span>
                                Principais Insights
                              </h4>
                              <div className="space-y-3">
                                {reportData.flowNodes && reportData.flowNodes.length > 0 && (() => {
                                  // Calcular nó com maior drop-off
                                  const nodeStats = reportData.flowNodes.map((node, index) => {
                                    const reachedCount = reportData.campaign.sessions.filter((session) =>
                                      session.visitedNodes?.[node.id]
                                    ).length;
                                    const prevNode = index > 0 ? (reportData.flowNodes as NonNullable<typeof reportData.flowNodes>)[index - 1] : null;
                                    const prevReached = prevNode ? reportData.campaign.sessions.filter((session) =>
                                      session.visitedNodes?.[prevNode.id]
                                    ).length : reportData.stats.total;
                                    const dropOff = prevReached > 0 ? ((prevReached - reachedCount) / prevReached) * 100 : 0;
                                    return { node, dropOff, reachedCount, index };
                                  });

                                  const maxDropOff = nodeStats.reduce((max, curr) => curr.dropOff > max.dropOff ? curr : max, nodeStats[0]);
                                  const successRate = reportData.stats.total > 0 ? (reportData.stats.completed / reportData.stats.total) * 100 : 0;

                                  return (
                                    <>
                                      <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                                        <span className="text-2xl">📊</span>
                                        <div className="flex-1">
                                          <p className="text-sm font-bold text-white">Performance Geral</p>
                                          <p className="text-xs text-gray-300 mt-1">
                                            {successRate >= 70 ? '✅ Excelente! ' : successRate >= 40 ? '⚠️ Razoável. ' : '❌ Crítico! '}
                                            Taxa de conclusão de <strong className="text-blue-300">{successRate.toFixed(1)}%</strong>
                                          </p>
                                        </div>
                                      </div>

                                      {maxDropOff.dropOff > 10 && (
                                        <div className="flex items-start gap-3 p-3 bg-orange-500/10 border border-orange-500/20 rounded-xl">
                                          <span className="text-2xl">⚠️</span>
                                          <div className="flex-1">
                                            <p className="text-sm font-bold text-white">Maior Abandono</p>
                                            <p className="text-xs text-gray-300 mt-1">
                                              Etapa {maxDropOff.index + 1} ({maxDropOff.node.label}) perdeu <strong className="text-orange-300">{maxDropOff.dropOff.toFixed(1)}%</strong> dos contatos
                                            </p>
                                          </div>
                                        </div>
                                      )}

                                      {reportData.stats.failed > 0 && (
                                        <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                                          <span className="text-2xl">🔧</span>
                                          <div className="flex-1">
                                            <p className="text-sm font-bold text-white">Atenção Necessária</p>
                                            <p className="text-xs text-gray-300 mt-1">
                                              {reportData.stats.failed} {reportData.stats.failed === 1 ? 'sessão falhou' : 'sessões falharam'}. Revise configurações.
                                            </p>
                                          </div>
                                        </div>
                                      )}

                                      {reportData.stats.expired > 0 && (
                                        <div className="flex items-start gap-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
                                          <span className="text-2xl">⏰</span>
                                          <div className="flex-1">
                                            <p className="text-sm font-bold text-white">Sessões Expiradas</p>
                                            <p className="text-xs text-gray-300 mt-1">
                                              {reportData.stats.expired} {reportData.stats.expired === 1 ? 'sessão expirou' : 'sessões expiraram'} sem interação.
                                            </p>
                                          </div>
                                        </div>
                                      )}

                                      {successRate >= 70 && reportData.stats.failed === 0 && (
                                        <div className="flex items-start gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
                                          <span className="text-2xl">🎉</span>
                                          <div className="flex-1">
                                            <p className="text-sm font-bold text-white">Campanha de Sucesso!</p>
                                            <p className="text-xs text-gray-300 mt-1">
                                              Excelente performance sem erros. Continue assim!
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>

                          {/* Resumo de Status */}
                          <div className="bg-white/5 rounded-2xl border border-white/10 p-6 shadow-lg backdrop-blur-sm">
                            <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2 font-oswald">
                              <span className="text-2xl">📋</span>
                              Resumo de Status das Sessões
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div className="text-center p-4 bg-white/5 rounded-xl border border-white/10">
                                <div className="text-3xl mb-2">📊</div>
                                <div className="text-2xl font-bold text-white">{reportData.stats.total}</div>
                                <div className="text-sm text-gray-400 font-medium">Total</div>
                              </div>
                              <div className="text-center p-4 bg-green-500/10 rounded-xl border border-green-500/20">
                                <div className="text-3xl mb-2">✅</div>
                                <div className="text-2xl font-bold text-green-400">{reportData.stats.active}</div>
                                <div className="text-sm text-green-300 font-medium">Ativas</div>
                              </div>
                              <div className="text-center p-4 bg-purple-500/10 rounded-xl border border-purple-500/20">
                                <div className="text-3xl mb-2">🎯</div>
                                <div className="text-2xl font-bold text-purple-400">{reportData.stats.completed}</div>
                                <div className="text-sm text-purple-300 font-medium">Concluídas</div>
                              </div>
                              <div className="text-center p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
                                <div className="text-3xl mb-2">⏰</div>
                                <div className="text-2xl font-bold text-yellow-400">{reportData.stats.expired}</div>
                                <div className="text-sm text-yellow-300 font-medium">Expiradas</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Tab Detalhes */}
                      {activeTab === 'details' && (
                        <div className="space-y-6">
                          {/* Informações da Campanha */}
                          <div className="bg-white/5 rounded-2xl p-6 border border-white/10 backdrop-blur-sm">
                            <h4 className="text-lg font-bold text-white mb-4 font-oswald">{reportData.campaign.name}</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="font-medium text-gray-400">Status:</span>
                                <span className={`ml-2 inline-flex items-center px-2 py-1 rounded text-xs font-bold ${reportData.campaign.status === 'DRAFT' ? 'bg-gray-500/20 text-gray-300 border border-gray-500/30' :
                                  reportData.campaign.status === 'SCHEDULED' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
                                    reportData.campaign.status === 'STARTED' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                                      reportData.campaign.status === 'PAUSED' ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' :
                                        'bg-green-500/20 text-green-300 border border-green-500/30'
                                  }`}>
                                  {reportData.campaign.status === 'DRAFT' ? 'Rascunho' :
                                    reportData.campaign.status === 'SCHEDULED' ? 'Agendada' :
                                      reportData.campaign.status === 'STARTED' ? 'Iniciada' :
                                        reportData.campaign.status === 'PAUSED' ? 'Pausada' : 'Concluída'}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-400">Conexão:</span>
                                <span className="ml-2 text-white">
                                  {reportData.campaign.connection?.instanceName || 'N/A'}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-400">Data de Criação:</span>
                                <span className="ml-2 text-white">{new Date(reportData.campaign.createdAt).toLocaleString('pt-BR')}</span>
                              </div>
                            </div>
                          </div>

                          {/* Estatísticas */}
                          <div>
                            <h4 className="text-lg font-bold text-white mb-4 font-oswald">Estatísticas de Sessões</h4>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                              <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10">
                                <div className="text-2xl font-bold text-white">{reportData.stats.total}</div>
                                <div className="text-sm text-gray-400">Total de Contatos</div>
                              </div>
                              <div className="bg-green-500/10 rounded-xl p-4 text-center border border-green-500/20">
                                <div className="text-2xl font-bold text-green-400">{reportData.stats.active}</div>
                                <div className="text-sm text-green-300">Sessões Ativas</div>
                              </div>
                              <div className="bg-purple-500/10 rounded-xl p-4 text-center border border-purple-500/20">
                                <div className="text-2xl font-bold text-purple-400">{reportData.stats.completed}</div>
                                <div className="text-sm text-purple-300">Concluídas</div>
                              </div>
                              <div className="bg-red-500/10 rounded-xl p-4 text-center border border-red-500/20">
                                <div className="text-2xl font-bold text-red-400">{reportData.stats.failed}</div>
                                <div className="text-sm text-red-300">Falhadas</div>
                              </div>
                              <div className="bg-yellow-500/10 rounded-xl p-4 text-center border border-yellow-500/20">
                                <div className="text-2xl font-bold text-yellow-400">{reportData.stats.expired}</div>
                                <div className="text-sm text-yellow-300">Expiradas</div>
                              </div>
                            </div>
                          </div>

                          {/* Sessões Detalhadas com Nós */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-lg font-bold text-white font-oswald">Detalhes das Sessões por Nó</h4>
                              <button
                                onClick={handleExportToExcel}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                                title="Exportar para Excel"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="font-medium">Exportar Excel</span>
                              </button>
                            </div>
                            <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto overflow-y-auto max-h-[400px]">
                              <table className="min-w-full divide-y divide-white/10 table-auto">
                                <thead className="bg-white/10">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider sticky left-0 bg-[#1e3a5f] z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]">Nome</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider sticky left-32 bg-[#1e3a5f] z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]">Telefone</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider bg-white/10">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider bg-white/10">Motivo</th>
                                    {/* Colunas dinâmicas para cada nó do fluxo */}
                                    {reportData.flowNodes && reportData.flowNodes.map((node) => (
                                      <th key={node.id} className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider min-w-[120px]">
                                        <div className="flex flex-col items-center">
                                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold mb-1 ${node.type === 'text' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                                            node.type === 'image' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                                              node.type === 'video' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
                                                node.type === 'audio' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
                                                  node.type === 'document' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
                                                    'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                                            }`}>
                                            {node.type}
                                          </span>
                                          <span className="text-xs text-gray-400 truncate max-w-[100px]" title={node.label}>
                                            {node.label}
                                          </span>
                                        </div>
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="bg-transparent divide-y divide-white/10">
                                  {reportData.campaign.sessions.map((session) => (
                                    <tr key={session.id} className="hover:bg-white/5">
                                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white sticky left-0 bg-[#1e3a5f] z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]">{session.contactName}</td>
                                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300 sticky left-32 bg-[#1e3a5f] z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]">{cleanPhoneNumber(session.contactPhone)}</td>
                                      <td className="px-4 py-3 whitespace-nowrap bg-transparent">
                                        <span
                                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold cursor-help ${session.status === 'ACTIVE' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                                            session.status === 'COMPLETED' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
                                              session.status === 'FAILED' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
                                                'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                                            }`}
                                          title={session.status === 'FAILED' && session.variables?.errorReason
                                            ? `❌ ${String(session.variables.errorReason)}`
                                            : session.status === 'ACTIVE' ? 'Sessão ativa' :
                                              session.status === 'COMPLETED' ? 'Fluxo concluído' :
                                                session.status === 'FAILED' ? 'Falha no envio' : 'Sessão expirada'}
                                        >
                                          {session.status === 'ACTIVE' ? 'Ativa' :
                                            session.status === 'COMPLETED' ? 'Concluída' :
                                              session.status === 'FAILED' ? 'Falhada' : 'Expirada'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 bg-transparent max-w-xs">
                                        {session.status === 'FAILED' && session.variables?.errorReason ? (
                                          <div className="flex items-start gap-2">
                                            <span className="text-red-400 flex-shrink-0 mt-0.5">⚠️</span>
                                            <span className="text-xs text-red-300 break-words">{String(session.variables.errorReason)}</span>
                                          </div>
                                        ) : (
                                          <span className="text-xs text-gray-500">-</span>
                                        )}
                                      </td>
                                      {/* Colunas dinâmicas com status de cada nó */}
                                      {reportData.flowNodes && reportData.flowNodes.map((node) => {
                                        const nodeData = session.visitedNodes?.[node.id];
                                        return (
                                          <td key={node.id} className="px-4 py-3 text-center">
                                            {nodeData ? (
                                              <div className="flex flex-col items-center gap-1">
                                                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-bold ${nodeData.sent ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'
                                                  }`}>
                                                  {nodeData.sent ? '✓ Enviado' : '✗ Falhou'}
                                                </span>
                                                {nodeData.visitedAt && (
                                                  <span className="text-xs text-gray-400">
                                                    {new Date(nodeData.visitedAt).toLocaleTimeString('pt-BR')}
                                                  </span>
                                                )}
                                                {nodeData.error && (
                                                  <span className="text-xs text-red-400 truncate max-w-[100px]" title={nodeData.error}>
                                                    {nodeData.error}
                                                  </span>
                                                )}
                                              </div>
                                            ) : (
                                              <span className="text-gray-600 text-xs">-</span>
                                            )}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      Nenhum dado disponível
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Portal>
        )}

        {/* Modal de Criar Campanha */}
        <InteractiveCampaignModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateCampaign}
        />

        {/* Diálogo de Confirmação Padronizado */}
        <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}>
          <AlertDialogContent className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] p-8 backdrop-blur-3xl shadow-2xl max-w-md">
            <AlertDialogHeader className="space-y-4">
              <AlertDialogTitle className="text-2xl font-bold font-oswald text-white uppercase tracking-tight flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${confirmDialog.type === 'danger' ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-blue-500/10 border-blue-500/20 text-blue-400'}`}>
                  {confirmDialog.type === 'danger' ? '⚠️' : 'ℹ️'}
                </div>
                {confirmDialog.title}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400 font-oswald uppercase tracking-[0.1em] text-[10px] font-bold leading-relaxed">
                {confirmDialog.message}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-8 flex gap-3">
              <AlertDialogCancel className="flex-1 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-[10px] font-bold font-oswald text-gray-400 uppercase tracking-[0.2em] transition-all">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDialog.onConfirm}
                className={`flex-1 px-8 py-4 rounded-2xl text-[10px] font-bold font-oswald text-white uppercase tracking-[0.2em] transition-all border shadow-lg ${confirmDialog.type === 'danger'
                  ? 'bg-red-600 hover:bg-red-500 border-red-500 shadow-red-900/20'
                  : 'bg-blue-600 hover:bg-blue-500 border-blue-500 shadow-blue-900/20'
                  }`}
              >
                {confirmDialog.confirmText || 'Confirmar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
